import React, { useState } from 'react';
import { UserPlus, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useProjects } from '@/hooks/useProjects';

interface InviteUserButtonProps {
  compactSidebar?: boolean;
}

export const InviteUserButton: React.FC<InviteUserButtonProps> = ({ compactSidebar }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('miembro');
  const [isLoading, setIsLoading] = useState(false);
  
  const { projects, loading: projectsLoading } = useProjects();

  const handleInvite = async () => {
    if (!email.trim()) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    if (!selectedProjectId) {
      toast.error('Por favor selecciona un proyecto');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: { 
          email: email.trim(),
          projectId: selectedProjectId,
          role: selectedRole
        }
      });

      if (error) {
        throw error;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      toast.success('¡Invitación enviada exitosamente!');
      setEmail('');
      setSelectedProjectId('');
      setSelectedRole('miembro');
      setIsOpen(false);
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast.error(error.message || 'Error al enviar la invitación');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size={compactSidebar ? "icon" : "sm"}
          className="bg-primary text-primary-foreground hover:bg-primary/90 border-primary"
        >
          <UserPlus className="h-4 w-4" />
          {!compactSidebar && <span className="ml-2">Invitar Usuario</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Invitar Usuario a Proyecto
          </DialogTitle>
          <DialogDescription>
            Envía una invitación por email y asigna acceso a un proyecto específico.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="email">Email del usuario</Label>
            <Input
              id="email"
              type="email"
              placeholder="usuario@ejemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="project">Proyecto</Label>
            <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
              <SelectTrigger>
                <SelectValue placeholder={projectsLoading ? "Cargando proyectos..." : "Selecciona un proyecto"} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="role">Rol en el proyecto</Label>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consultor">Consultor (solo lectura)</SelectItem>
                <SelectItem value="miembro">Miembro (puede modificar)</SelectItem>
                <SelectItem value="líder">Líder (gestión completa)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleInvite} disabled={isLoading || !selectedProjectId}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Enviar Invitación
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};